import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Trophy, Crown, Medal, Monitor, RefreshCw, ChevronDown } from "lucide-react";
import { API } from "../App";
import axios from "axios";

export default function NowDancingPage() {
  const { competitionId } = useParams();
  const [data, setData] = useState(null);
  const [rankings, setRankings] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [nowRes, rankRes] = await Promise.all([
        axios.get(`${API}/now-dancing/${competitionId}`),
        axios.get(`${API}/rankings/${competitionId}`)
      ]);
      setData(nowRes.data);
      setRankings(rankRes.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [competitionId]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 3000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-20 h-20 border-4 border-[#FFD700] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const { competition, festival, active_heat, pairs } = data || {};
  const festivalLogo = festival?.logo_base64;

  const getRoundName = () => {
    if (!competition?.rounds || !active_heat) return "";
    const roundIndex = active_heat.round_number - 1;
    return competition.rounds[roundIndex] || `Round ${active_heat.round_number}`;
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-[#FFD700]" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-[#C0C0C0]" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-[#CD7F32]" />;
    return <span className="w-6 h-6 flex items-center justify-center text-[#52525B] font-bold">{rank}</span>;
  };

  const leaders = rankings?.leaders || [];
  const followers = rankings?.followers || [];

  if (!active_heat) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-8">
        <div className="w-full max-w-6xl flex items-center justify-between gap-8">
          {/* Left: J&J logo + URL */}
          <div className="flex-shrink-0 w-44 md:w-64 flex flex-col items-center">
            <img src="/jj-logo.png" alt="J&J" className="w-full object-contain opacity-95" />
            <p className="mt-3 text-[#A1A1AA] text-sm md:text-base font-bold tracking-wide" data-testid="waiting-jj-url">
              jackandjillscore.com
            </p>
          </div>

          {/* Center: Waiting message */}
          <div className="flex-1 text-center">
            <motion.div
              className="mb-8"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Users className="w-32 h-32 text-[#27272a] mx-auto" />
            </motion.div>
            <h1 className="font-['Playfair_Display'] text-4xl text-white mb-4">
              Waiting for Next Heat
            </h1>
            <p className="text-[#52525B] text-xl">{festival?.name}</p>
          </div>

          {/* Right: Festival logo */}
          <div className="flex-shrink-0 w-44 md:w-64">
            {festivalLogo ? (
              <img
                src={festivalLogo}
                alt={festival?.name || 'Event'}
                className="w-full object-contain rounded-lg"
                data-testid="waiting-festival-logo"
              />
            ) : (
              <div className="w-full" />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black">
      {/* ============ SECTION 1: NOW DANCING (Full Viewport) ============ */}
      <section className="min-h-screen flex flex-col relative" data-testid="now-dancing-section">
        {/* J&J Platform Watermark - fixed center of viewport (visible during scroll) */}
        <div className="fixed inset-0 flex items-center justify-center z-0 pointer-events-none overflow-hidden">
          <img src="/jj-logo.png" alt="" className="w-[480px] h-[480px] md:w-[640px] md:h-[640px] object-contain opacity-[0.11]" />
        </div>

        {/* Festival Event Logo - top header bar */}
        {festivalLogo && (
          <div className="py-4 flex items-center justify-center gap-4 border-b border-[#27272a]/50 bg-black/40 relative z-10">
            <img src={festivalLogo} alt="Event" className="h-24 md:h-[7.5rem] object-contain rounded-lg" />
          </div>
        )}

        {/* Header */}
        <header className="py-4 text-center border-b border-[#27272a] relative z-10">
          <p className="text-[#A1A1AA] text-lg mb-1">{festival?.name}</p>
          <h2 className="text-[#FFD700] text-2xl font-bold">{competition?.category}</h2>
        </header>

        {/* Heat Banner */}
        <div className="py-3 bg-[#FF007F] relative z-10">
          <div className="absolute left-4 top-1/2 -translate-y-1/2">
            <img src="/jj-logo.png" alt="J&J" className="w-16 h-16 md:w-20 md:h-20 opacity-95" />
          </div>
          <div className="text-center">
            <p className="text-white/80 text-sm uppercase tracking-widest mb-1">{getRoundName() || "Active Heat"}</p>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-white">
              HEAT {active_heat.heat_number}
            </h1>
            <p className="text-white/80 mt-1">
              {pairs?.length || 0} Couples
            </p>
          </div>
        </div>

        {/* NOW DANCING + Pairs */}
        <main className="flex-1 flex flex-col items-center justify-center p-6 relative z-10">
          <div className="mb-10">
            <motion.h1 
              className="font-['Playfair_Display'] text-5xl md:text-7xl font-bold text-white text-center"
              animate={{ opacity: [0.7, 1, 0.7] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              NOW DANCING
            </motion.h1>
          </div>

          <div className="w-full max-w-5xl">
            <AnimatePresence mode="popLayout">
              {pairs?.map((pair, index) => {
                const hasLeader = !!pair.leader_id;
                const hasFollower = !!pair.follower_id;
                return (
                <motion.div
                  key={`${pair.leader_id || 'x'}-${pair.follower_id || 'x'}-${index}`}
                  className="flex items-center justify-center gap-6 md:gap-12 mb-6"
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -40 }}
                  transition={{ delay: index * 0.08 }}
                >
                  {/* Leader (or placeholder when solo follower) */}
                  <div className="text-center flex-1">
                    {hasLeader ? (
                      <div className="inline-flex items-center gap-4">
                        <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl bg-[#FFD700]/15 flex items-center justify-center">
                          <span className="text-[#FFD700] text-2xl md:text-3xl font-bold">
                            #{pair.leader_bib || '?'}
                          </span>
                        </div>
                        <div className="text-left">
                          <p className="text-[#FFD700] text-xs uppercase tracking-widest">Leader</p>
                          <p className="text-white text-lg md:text-2xl font-bold">{pair.leader_name}</p>
                        </div>
                      </div>
                    ) : (
                      <span className="text-[#52525B] text-sm italic">Solo Follower</span>
                    )}
                  </div>

                  {/* Divider */}
                  <div className="text-[#52525B] text-3xl font-light">{hasLeader && hasFollower ? "&" : "•"}</div>

                  {/* Follower (or placeholder when solo leader) */}
                  <div className="text-center flex-1">
                    {hasFollower ? (
                      <div className="inline-flex items-center gap-4">
                        <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl bg-[#FF007F]/15 flex items-center justify-center">
                          <span className="text-[#FF007F] text-2xl md:text-3xl font-bold">
                            #{pair.follower_bib || '?'}
                          </span>
                        </div>
                        <div className="text-left">
                          <p className="text-[#FF007F] text-xs uppercase tracking-widest">Follower</p>
                          <p className="text-white text-lg md:text-2xl font-bold">{pair.follower_name}</p>
                        </div>
                      </div>
                    ) : (
                      <span className="text-[#52525B] text-sm italic">Solo Leader</span>
                    )}
                  </div>
                </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          {/* Footer - JackAndJillScore branding (same font size as dancer names) */}
          <div className="w-full max-w-5xl text-center mt-12 mb-4 relative z-10">
            <p className="text-[#A1A1AA] text-lg md:text-2xl font-bold tracking-wide" data-testid="now-dancing-footer">
              jackandjillscore.com — Web App
            </p>
          </div>
        </main>

      </section>
    </div>
  );
}
